import { CSSProperties, RefObject, useCallback, useRef, useState } from 'react';
import { downloadBlob } from 'src/utils/file';

export interface HtmlTemplateExportOutput {
  templateRef: RefObject<HTMLDivElement | null>;
  templateStyle: CSSProperties;
  isExporting: boolean;
  prepareTemplate: () => void;
  exportTemplate: () => Promise<void>;
}

export const hiddenTemplateStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  // Keep the template out of layout/interaction while still renderable by html2canvas.
  opacity: 0,
  pointerEvents: 'none',
};

const waitForImages = async (element: HTMLElement) => {
  const images = Array.from(element.querySelectorAll('img'));

  await Promise.all(
    images.map(async (image) => {
      if (image.complete) {
        return;
      }

      await new Promise<void>((resolve) => {
        const done = () => resolve();

        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      });
    }),
  );
};

export const renderHtmlElementToCanvas = async (element: HTMLElement) => {
  await waitForImages(element);
  const templateId = element.getAttribute('id') ?? '';

  // Lazy-load html2canvas only when export is triggered to keep it out of the main bundle
  const { default: html2canvas } = await import('html2canvas');

  return html2canvas(element, {
    onclone: (clonedDoc) => {
      // Reveal the cloned template for rendering while keeping the on-screen copy hidden.
      const clonedElement = clonedDoc.getElementById(templateId);
      if (clonedElement) {
        clonedElement.style.opacity = '1';
      }
    },
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
    scale: 1,
    useCORS: true,
    backgroundColor: '#ffffff',
  });
};

export const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve);
  });

const downloadCanvas = async (templateName: string, canvas: HTMLCanvasElement) => {
  const blob = await canvasToBlob(canvas);
  if (!blob) {
    return;
  }

  downloadBlob(blob, `${templateName}.png`);
};

export const useHtmlTemplateExport = (exportedFileName: string): HtmlTemplateExportOutput => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const prepareTemplate = useCallback(() => {
    setIsExporting(true);
  }, []);

  const exportTemplate = useCallback(async () => {
    const templateElement = templateRef.current;
    if (!templateElement) {
      setIsExporting(false);
      return;
    }

    const canvas = await renderHtmlElementToCanvas(templateElement);
    if (!canvas) {
      setIsExporting(false);
      return;
    }

    // Export with a stable id to ensure the cloned DOM resolves correctly.
    await downloadCanvas(exportedFileName, canvas);
    setIsExporting(false);
  }, [exportedFileName]);

  return {
    templateRef,
    templateStyle: hiddenTemplateStyle,
    isExporting,
    prepareTemplate,
    exportTemplate,
  };
};
